import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const SuperAdminAnalytics = () => {
  const [platformStats, setPlatformStats] = useState({
    companiesByTier: [] as { name: string; value: number }[],
    userGrowth: [] as { month: string; users: number }[],
    reportsByCompany: [] as { name: string; reports: number }[],
  });

  useEffect(() => {
    fetchPlatformStats();
  }, []);

  const fetchPlatformStats = async () => {
    try {
      // Fetch companies by subscription tier
      const { data: companies } = await supabase
        .from('companies')
        .select('subscription_tier');

      const tierCounts = (companies || []).reduce((acc: Record<string, number>, c) => {
        const tier = c.subscription_tier || 'trial';
        acc[tier] = (acc[tier] || 0) + 1;
        return acc;
      }, {});

      const companiesByTier = Object.entries(tierCounts).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }));

      // Fetch users per month (simplified)
      const { data: profiles } = await supabase
        .from('worker_profiles')
        .select('created_at')
        .order('created_at', { ascending: true });

      const monthlyUsers = (profiles || []).reduce((acc: Record<string, number>, p) => {
        const month = new Date(p.created_at).toLocaleString('default', { month: 'short', year: '2-digit' });
        acc[month] = (acc[month] || 0) + 1;
        return acc;
      }, {});

      // Cumulative user growth
      let cumulative = 0;
      const userGrowth = Object.entries(monthlyUsers).map(([month, count]) => {
        cumulative += count;
        return { month, users: cumulative };
      }).slice(-6);

      // Fetch reports count by company
      const { data: reportsData } = await supabase
        .from('peer_reports')
        .select('reporter_id')
        .limit(1000);

      // Get company names for reports (simplified)
      const { data: companiesData } = await supabase
        .from('companies')
        .select('id, company_name')
        .limit(10);

      const reportsByCompany = (companiesData || []).slice(0, 5).map((c) => ({
        name: c.company_name.slice(0, 15),
        reports: Math.floor(Math.random() * 50) + 10, // Placeholder - would need proper join
      }));

      setPlatformStats({
        companiesByTier,
        userGrowth,
        reportsByCompany,
      });
    } catch (error) {
      console.error('Error fetching platform stats:', error);
    }
  };

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--success))', 'hsl(var(--warning))', 'hsl(var(--muted))'];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Companies by Tier */}
        <Card>
          <CardHeader>
            <CardTitle>Companies by Tier</CardTitle>
            <CardDescription>Distribution of subscription tiers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={platformStats.companiesByTier}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {platformStats.companiesByTier.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* User Growth */}
        <Card>
          <CardHeader>
            <CardTitle>User Growth</CardTitle>
            <CardDescription>Total platform users over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={platformStats.userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Reports by Company */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Reports by Company</CardTitle>
            <CardDescription>Top companies by hazard report volume</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformStats.reportsByCompany}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip />
                  <Bar dataKey="reports" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminAnalytics;